import { useState } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { X, Save, Trash2, AlertTriangle } from 'lucide-react';

const EditUserModal = ({ user, onClose, onUpdate, isSuperAdmin }) => {
  const [formData, setFormData] = useState({
    name: user.user.name,
    email: user.user.email,
    credits: user.user.credits || 0,
    role: user.user.role
  });
  const [loading, setLoading] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'credits' ? parseInt(value) || 0 : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Only include credits in update if super admin
      const updateData = {
        name: formData.name,
        email: formData.email,
        role: formData.role
      };
      if (isSuperAdmin) {
        updateData.credits = formData.credits;
      }
      
      const response = await axios.put(`${API}/admin/users/${user.user.id}`, updateData);
      toast.success('User updated successfully');
      onUpdate(response.data);
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (deleteConfirmText !== 'DELETE') {
      toast.error('Please type DELETE to confirm');
      return;
    }

    setLoading(true);
    try {
      await axios.delete(`${API}/admin/users/${user.user.id}`);
      toast.success('User deleted successfully');
      onUpdate(null); // Signal deletion
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <Card className="max-w-2xl w-full max-h-[90vh] overflow-y-auto relative">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 z-10"
          data-testid="close-edit-modal"
        >
          <X className="w-6 h-6" />
        </button>

        <CardHeader>
          <CardTitle className="text-2xl">Edit User Profile</CardTitle>
          <CardDescription>Update user information and manage account</CardDescription>
        </CardHeader>

        <CardContent>
          {!showDeleteConfirm ? (
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Name */}
              <div>
                <Label htmlFor="name">Full Name</Label>
                <Input
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  data-testid="edit-name"
                />
              </div>

              {/* Email */}
              <div>
                <Label htmlFor="email">Email Address</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  data-testid="edit-email"
                />
              </div>

              {/* Credits - Only Super Admin can edit */}
              {isSuperAdmin ? (
                <div>
                  <Label htmlFor="credits">Credits</Label>
                  <Input
                    id="credits"
                    name="credits"
                    type="number"
                    min="0"
                    value={formData.credits}
                    onChange={handleChange}
                    data-testid="edit-credits"
                  />
                  <p className="text-sm text-gray-500 mt-1">
                    Current credits: {user.user.credits || 0}
                  </p>
                </div>
              ) : (
                <div>
                  <Label>Credits</Label>
                  <p className="text-sm text-gray-600 py-2 px-3 bg-gray-100 rounded-md">
                    {user.user.credits || 0} credits
                  </p>
                  <p className="text-xs text-gray-400 mt-1">
                    Only super admin can modify credits
                  </p>
                </div>
              )}

              {/* Role */}
              <div>
                <Label htmlFor="role">Role</Label>
                <select
                  id="role"
                  name="role"
                  value={formData.role}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  data-testid="edit-role"
                >
                  <option value="volunteer">Volunteer</option>
                  <option value="admin">Administrator</option>
                </select>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-4 pt-4">
                <Button
                  type="submit"
                  disabled={loading}
                  className="flex-1 bg-indigo-600 hover:bg-indigo-700"
                  data-testid="save-user-btn"
                >
                  <Save className="w-4 h-4 mr-2" />
                  {loading ? 'Saving...' : 'Save Changes'}
                </Button>
                <Button
                  type="button"
                  onClick={onClose}
                  variant="outline"
                  disabled={loading}
                >
                  Cancel
                </Button>
              </div>

              {/* Delete Section */}
              <div className="pt-6 border-t">
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <h4 className="font-semibold text-red-800 mb-2 flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5" />
                    Danger Zone
                  </h4>
                  <p className="text-sm text-red-700 mb-3">
                    Deleting this user will permanently remove their account, assessment results, and payment history. This action cannot be undone.
                  </p>
                  <Button
                    type="button"
                    onClick={() => setShowDeleteConfirm(true)}
                    variant="destructive"
                    className="bg-red-600 hover:bg-red-700"
                    data-testid="show-delete-btn"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete User Account
                  </Button>
                </div>
              </div>
            </form>
          ) : (
            // Delete Confirmation
            <div className="space-y-6">
              <div className="bg-red-50 border-2 border-red-300 rounded-lg p-6">
                <div className="flex items-start gap-3 mb-4">
                  <AlertTriangle className="w-8 h-8 text-red-600 flex-shrink-0" />
                  <div>
                    <h3 className="text-xl font-bold text-red-800 mb-2">
                      Confirm Account Deletion
                    </h3>
                    <p className="text-red-700 mb-4">
                      You are about to permanently delete:
                    </p>
                    <div className="bg-white p-3 rounded border border-red-200 mb-4">
                      <p className="font-semibold">{user.user.name}</p>
                      <p className="text-sm text-gray-600">{user.user.email}</p>
                    </div>
                    <p className="text-red-700 font-semibold mb-2">
                      This will permanently delete:
                    </p>
                    <ul className="list-disc list-inside text-red-700 space-y-1 mb-4">
                      <li>User account and profile</li>
                      <li>Assessment results and scores</li>
                      <li>Payment transaction history</li>
                      <li>All associated data</li>
                    </ul>
                    <p className="text-red-800 font-bold">
                      This action CANNOT be undone!
                    </p>
                  </div>
                </div>

                <div className="mb-4">
                  <Label htmlFor="delete-confirm" className="text-red-800 font-semibold">
                    Type "DELETE" to confirm:
                  </Label>
                  <Input
                    id="delete-confirm"
                    value={deleteConfirmText}
                    onChange={(e) => setDeleteConfirmText(e.target.value)}
                    placeholder="Type DELETE"
                    className="border-red-300 focus:ring-red-500"
                    data-testid="delete-confirm-input"
                  />
                </div>

                <div className="flex gap-3">
                  <Button
                    onClick={handleDelete}
                    disabled={loading || deleteConfirmText !== 'DELETE'}
                    variant="destructive"
                    className="flex-1 bg-red-600 hover:bg-red-700"
                    data-testid="confirm-delete-btn"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    {loading ? 'Deleting...' : 'Delete Permanently'}
                  </Button>
                  <Button
                    onClick={() => {
                      setShowDeleteConfirm(false);
                      setDeleteConfirmText('');
                    }}
                    variant="outline"
                    disabled={loading}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default EditUserModal;
